<template>
  <div class="mc-page">
    <!-- Header -->
    <header class="mc-header">
      <button class="mc-header__back" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </button>
      <h1 class="mc-header__title">{{ $lang.mc_title }}</h1>
      <div class="mc-header__spacer"></div>
    </header>

    <!-- Tab Toggle -->
    <div class="mc-tabs">
      <button
        class="mc-tabs__btn"
        :class="{ 'mc-tabs__btn--active': activeTab === 'newbie' }"
        @click="switchTab('newbie')"
      >{{ $lang.mc_newbie || 'Newbie' }}</button>
      <button
        class="mc-tabs__btn"
        :class="{
          'mc-tabs__btn--active': activeTab === 'daily',
          'mc-tabs__btn--locked': !newbieCompleted
        }"
        @click="switchTab('daily')"
      >
        <van-icon v-if="!newbieCompleted" name="lock" size="12" style="margin-right: 4px;" />
        {{ $lang.mc_daily }}
      </button>
      <button
        class="mc-tabs__btn"
        :class="{
          'mc-tabs__btn--active': activeTab === 'weekly',
          'mc-tabs__btn--locked': !newbieCompleted
        }"
        @click="switchTab('weekly')"
      >
        <van-icon v-if="!newbieCompleted" name="lock" size="12" style="margin-right: 4px;" />
        {{ $lang.mc_weekly }}
      </button>
    </div>

    <div v-if="loading" class="mc-loading">
      <van-loading color="#ff5a1f" size="32" />
    </div>

    <template v-else>
      <div v-if="readFailureMessage" class="mc-read-warning">
        {{ readFailureMessage }}
      </div>
      <!-- Locked state for daily/weekly when newbie not completed -->
      <div v-if="activeTab !== 'newbie' && !newbieCompleted" class="mc-locked">
        <van-icon name="lock" size="48" color="#4b5563" />
        <p class="mc-locked__title">{{ $lang.mc_locked_title || 'Locked' }}</p>
        <p class="mc-locked__desc">{{ $lang.mc_locked_desc || 'Complete all newbie tasks to unlock' }}</p>
        <button class="mc-locked__btn" @click="switchTab('newbie')">{{ $lang.mc_go_newbie || 'Go to Newbie Tasks' }}</button>
      </div>

      <template v-else>
      <!-- Cumulative Rewards -->
      <section class="mc-cumulative">
        <div class="mc-cumulative__header">
          <p class="mc-cumulative__label">{{ $lang.mc_activity_points }}</p>
          <div class="mc-cumulative__reset">
            <van-icon name="clock-o" size="12" color="#9ca3af" />
            <span>{{ $lang.mc_reset_in }} {{ resetCountdown }}</span>
          </div>
        </div>
        <p class="mc-cumulative__score">
          <span class="mc-cumulative__current">{{ currentSection.currentPoints }}</span>
          <span class="mc-cumulative__sep"> / </span>
          <span class="mc-cumulative__total">{{ maxThreshold }}</span>
        </p>

        <!-- Milestone chests -->
        <div class="mc-milestones">
          <div
            v-for="m in currentSection.milestones"
            :key="m.threshold"
            class="mc-milestone"
            :class="{
              'mc-milestone--reached': currentSection.currentPoints >= m.threshold,
              'mc-milestone--claimed': m.claimed
            }"
            :style="{ left: (m.threshold / maxThreshold * 100) + '%' }"
            @click="claimMilestone(m)"
          >
            <div class="mc-milestone__chest">
              <img
                :src="currentSection.currentPoints >= m.threshold || m.claimed
                  ? require('../../../assets/img/activity/activity_icon/task/open.png')
                  : require('../../../assets/img/activity/activity_icon/task/close.png')"
                class="mc-milestone__img"
              />
              <van-icon v-if="m.claimed" name="success" class="mc-milestone__check" size="12" color="#ff5a1f" />
              <span v-if="m.rewardAmount" class="mc-milestone__reward">{{ getCurrency }} {{ $formatNumberWithCommas(m.rewardAmount) }}</span>
            </div>
            <span class="mc-milestone__pts">{{ m.threshold }}</span>
          </div>
        </div>

        <!-- Progress bar -->
        <div class="mc-bar">
          <div class="mc-bar__fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </section>

      <!-- Task List -->
      <section class="mc-tasks">
        <h3 class="mc-tasks__title">
          {{ activeTab === 'newbie' ? ($lang.mc_newbie_missions || 'Newbie Missions') : activeTab === 'daily' ? $lang.mc_todays_missions : $lang.mc_weekly_missions }}
        </h3>

        <div v-if="currentSection.tasks.length === 0" class="mc-empty">
          {{ readFailureMessage || ($lang.reward_no_data || 'No rewards found') }}
        </div>
        <template v-else>
        <div
            v-for="task in currentSection.tasks"
            :key="task.code"
            class="mc-task"
            :class="{
              'mc-task--claimable': task.status === 'claimable',
              'mc-task--claimed': task.status === 'claimed'
            }"
          >
          <div class="mc-task__icon">
            <img
              v-if="task.iconUrl"
              :src="task.iconUrl"
              class="mc-task__icon-img"
            />
            <van-icon v-else name="medal-o" size="24" color="#ff5a1f" />
          </div>
          <div class="mc-task__body">
            <p class="mc-task__name">{{ task.name }}</p>
            <p class="mc-task__progress">{{ task.progress }}</p>
          </div>
          <div class="mc-task__right">
            <div class="mc-task__rewards">
              <span class="mc-task__reward mc-task__reward--pts">+{{ task.rewardAmount }} AP</span>
              <span v-if="task.rewardGold" class="mc-task__reward mc-task__reward--gold">+{{ $formatNumberWithCommas(task.rewardGold) }} {{ getCurrency }}</span>
            </div>
            <button
              class="mc-task__btn"
              :class="{
                'mc-task__btn--claimable': task.status === 'claimable',
                'mc-task__btn--claimed': task.status === 'claimed'
              }"
              :disabled="task.status === 'claimed' || !!claimingTaskCode"
              @click="handleTaskAction(task)"
            >
              <template v-if="claimingTaskCode === task.code">{{ $lang.common_loading || 'Loading...' }}</template>
              <template v-else-if="task.status === 'claimable'">{{ $lang.mc_claim }}</template>
              <template v-else-if="task.status === 'claimed'">{{ $lang.mc_claimed }}</template>
              <template v-else>{{ $lang.mc_go }}</template>
            </button>
          </div>
        </div>
        </template>
      </section>

      <!-- Rules -->
      <section class="mc-rules">
        <div class="mc-rules__card">
          <p class="mc-rules__heading">{{ $lang.mc_rules_title }}</p>
          <ol class="mc-rules__list">
            <li>{{ $lang.mc_rule_1 }}</li>
            <li>{{ $lang.mc_rule_2 }}</li>
            <li>{{ $lang.mc_rule_3 }}</li>
            <li>{{ $lang.mc_rule_4 }}</li>
          </ol>
        </div>
      </section>
      </template>
    </template>
    <login v-model="showLogin" overlay></login>
  </div>
</template>

<script>
import { TaskCenterNewbieInit, TaskCenterDailyInit, TaskCenterWeeklyInit, ClaimTaskReward, ClaimProgressReward, WithdrawInit } from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import { getMineActionRoute } from '@/views/layout/mineNavigation'
import {
  failMessage as rewardFailMessage,
  hasExplicitRewardIssueProof as hasRewardIssueProof,
  hasUnavailableMarker,
  isClaimSuccess as rewardClaimSuccess,
  isNeedLogin,
  isOkResponse,
  responseContent
} from './rewardApi'

const TAB_TO_PROMO = {
  newbie: 'task_newbie',
  daily: 'task_daily',
  weekly: 'task_weekly'
}

export default {
  name: 'WeeklyMissions',
  data() {
    return {
      activeTab: 'newbie',
      loading: true,
      resetCountdown: '00:00:00',
      countdownTimer: null,
      resetTs: 0,
      newbieCompleted: false,
      dailyLoaded: false,
      weeklyLoaded: false,
      readFailureMessage: '',
      showLogin: false,
      claimingTaskCode: '',
      claimingMilestoneKey: '',
      newbie: {
        category: 'newbie',
        currentPoints: 0,
        totalPoints: 0,
        milestones: [],
        tasks: []
      },
      daily: {
        category: 'daily',
        currentPoints: 0,
        totalPoints: 0,
        milestones: [],
        tasks: []
      },
      weekly: {
        category: 'weekly',
        currentPoints: 0,
        totalPoints: 0,
        milestones: [],
        tasks: []
      }
    }
  },
  computed: {
    currentSection() {
      return this[this.activeTab]
    },
    maxThreshold() {
      const ms = this.currentSection.milestones
      if (!ms.length) return 3000
      return ms[ms.length - 1].threshold
    },
    progressPercent() {
      if (!this.maxThreshold) return 0
      return Math.min(
        Math.round((this.currentSection.currentPoints / this.maxThreshold) * 100),
        100
      )
    }
  },
  mounted() {
    reportPromoPanel(TAB_TO_PROMO[this.activeTab], 1)
    this.fetchData()
  },
  beforeDestroy() {
    reportPromoPanel(TAB_TO_PROMO[this.activeTab], 2)
    clearInterval(this.countdownTimer)
  },
  methods: {
    requireLogin() {
      this.showLogin = true
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      const defaultFallback = fallback || this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'
      const message = rewardFailMessage(res, defaultFallback)
      if (this.hasWriteGuardMarker(res) && message === defaultFallback) {
        return rewardFailMessage(this.hasReadOnlyModeMarker(res) ? { readOnly: true } : { mock: true }, defaultFallback)
      }
      return message
    },
    hasVisibleSectionData(type) {
      const section = this[type]
      return !!(section && (section.tasks.length || section.milestones.length || section.currentPoints > 0 || section.totalPoints > 0))
    },
    hasAnyVisibleSectionData() {
      return ['newbie', 'daily', 'weekly'].some(type => this.hasVisibleSectionData(type))
    },
    preserveReadFailureMessage(message) {
      return message || this.$lang.reward_no_data || 'No rewards found'
    },
    setReadFailure(res, fallback, preserve = this.hasAnyVisibleSectionData()) {
      const payload = res && res.response && res.response.data ? res.response.data : res
      if (hasUnavailableMarker(payload)) {
        this.readFailureMessage = ''
        return
      }
      this.readFailureMessage = this.preserveReadFailureMessage(this.failMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found'), preserve)
      this.$toast({ message: this.readFailureMessage, icon: 'cross' })
    },
    failCloseSection(type) {
      const section = this[type]
      if (!section) return
      section.tasks = section.tasks.map(task => {
        if (task.status !== 'claimable' && task.canClaimReward !== true) return task
        return {
          ...task,
          status: task.status === 'claimable' ? 'inProgress' : task.status,
          canClaimReward: false
        }
      })
      section.milestones = section.milestones.map(milestone => {
        if (milestone.canClaim !== true) return milestone
        return {
          ...milestone,
          canClaim: false
        }
      })
    },
    failCloseAllSections() {
      ['newbie', 'daily', 'weekly'].forEach(type => this.failCloseSection(type))
    },
    hasWriteGuardMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (hasUnavailableMarker(source)) return true
      const blockingKeys = ['readOnly', 'readOnlyMode', 'readonly', 'read_only', 'read_only_mode', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendBlocked', 'backend_blocked', 'backendFailure', 'backend_failure', 'rewardBackendBlocked', 'reward_backend_blocked', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'falseProof', 'false_proof', 'paymentStatus', 'payment_status', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed']
      const blockingTokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mock_fallback', 'read_only', 'read_only_mode', 'read only mode', 'read-only-mode', 'readonly', 'read only', 'read-only', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'writeproof:false', 'write_proof:false', 'write proof:false', 'write-proof:false', 'writeproof false', 'write proof false', 'write-proof-false', 'backend_blocked', 'backendblocked', 'backend blocked', 'backend-blocked', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', 'reward_backend_blocked', 'rewardbackendblocked', 'reward backend blocked', 'reward-backend-blocked', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'falseproof', 'false_proof', 'false proof', 'false-proof', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technicalexception', 'technical_exception', 'technical exception', 'technical-exception', '技术异常', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'payment status unconfirmed', 'payment-status-unconfirmed', 'unconfirmed']
      const isActive = value => value === true || value === 1 || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      const isFalseProof = value => value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase()) || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (['writeProof', 'write_proof', 'rewardProof', 'reward_proof'].some(key => isFalseProof(source[key]))) return true
      if (blockingKeys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.readOnlyMessage, source.unavailableReason].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasWriteGuardMarker(source[key], depth + 1))
    },
    hasReadOnlyModeMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      const isReadOnlyMode = value => ['readonlymode', 'read_only_mode', 'read only mode', 'read-only-mode'].some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (source.readOnlyMode === true || source.readOnlyMode === 1 || source.read_only_mode === true || source.read_only_mode === 1) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.readOnlyMessage, source.unavailableReason].some(isReadOnlyMode)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasReadOnlyModeMarker(source[key], depth + 1))
    },
    isClaimSuccess(res) {
      return !this.hasWriteGuardMarker(res) && rewardClaimSuccess(res) && this.hasRewardIssueProof(res)
    },
    hasRewardIssueProof(res) {
      return hasRewardIssueProof(res)
    },
    safeNumber(value, fallback = 0) {
      const n = Number(typeof value === 'string' ? value.replace(/,/g, '') : value)
      return Number.isFinite(n) ? n : fallback
    },
    firstValue(source, keys, fallback = '') {
      if (!source) return fallback
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      return fallback
    },
    firstArray(source, keys) {
      if (!source || typeof source !== 'object') return []
      for (const key of keys) {
        const value = source[key]
        if (Array.isArray(value)) return value
        if (value && typeof value === 'object') {
          for (const nestedKey of ['list', 'records', 'dataList', 'rows', 'items']) {
            if (Array.isArray(value[nestedKey])) return value[nestedKey]
          }
        }
      }
      return []
    },
    sectionData(content, key) {
      if (!content || typeof content !== 'object') return {}
      const section = content[key]
      if (section && typeof section === 'object' && !Array.isArray(section)) {
        return {
          ...content,
          ...section
        }
      }
      return content
    },
    hasReadableSectionPayload(data) {
      if (!data || typeof data !== 'object') return false
      return this.milestoneRows(data).length > 0 ||
        this.firstArray(data, ['tasks', 'taskList', 'task_list', 'taskRows', 'task_rows', 'records', 'list', 'dataList']).length > 0
    },
    preserveUnavailableSectionRead(type, res, data) {
      const preserve = this.hasVisibleSectionData(type)
      if (!preserve || this.hasReadableSectionPayload(data)) return false
      if (!this.hasWriteGuardMarker(res) && !this.hasWriteGuardMarker(data)) return false
      this.failCloseSection(type)
      this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found', true)
      return true
    },
    preservePartialReadFailure(type, hasPartialReadFailure, data) {
      return !!hasPartialReadFailure &&
        this.hasVisibleSectionData(type) &&
        !this.hasReadableSectionPayload(data)
    },
    shouldBlockSectionRead(res, data) {
      if (!this.hasWriteGuardMarker(res) && !this.hasWriteGuardMarker(data)) return false
      this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found', this.hasAnyVisibleSectionData())
      return true
    },
    isTrueFlag(value) {
      if (value === true || value === 1) return true
      return ['1', 'true', 'yes', 'on', 'claimable', 'available'].includes(String(value == null ? '' : value).trim().toLowerCase())
    },
    async fetchData() {
      try {
        // Step 1: Call newbie init first — get newbie tasks + newbieCompleted flag
        const res = await TaskCenterNewbieInit({})
        if (isNeedLogin(res)) {
          this.failCloseAllSections()
          this.requireLogin()
        } else if (isOkResponse(res)) {
          this.readFailureMessage = ''
          const c = responseContent(res)
          this.newbieCompleted = this.isTrueFlag(c.newbieCompleted)
          const writable = this.isRewardRowWritable(c)
          const newbieData = this.sectionData(c, 'newbie')
          if (this.shouldBlockSectionRead(res, newbieData)) return
          if (this.preserveUnavailableSectionRead('newbie', res, newbieData)) return
          this.parseSection('newbie', newbieData, 'newbie', writable)

          // Step 2: If newbie completed, fetch daily & weekly
          if (this.newbieCompleted) {
            await this.fetchDailyWeekly()
          }
        } else {
          this.failCloseAllSections()
          this.setReadFailure(res)
        }
      } catch (e) {
        this.failCloseAllSections()
        this.setReadFailure(e, this.$lang.mc_request_failed || this.$lang.request_failed || 'Mission request failed. Please try again later.')
      } finally {
        this.loading = false
      }
    },
    async fetchDailyWeekly() {
      try {
        const [dailyRes, weeklyRes] = await Promise.all([
          TaskCenterDailyInit({}),
          TaskCenterWeeklyInit({})
        ])
        const hasPartialReadFailure = isNeedLogin(dailyRes) ||
          isNeedLogin(weeklyRes) ||
          !isOkResponse(dailyRes) ||
          !isOkResponse(weeklyRes)
        if (isNeedLogin(dailyRes)) {
          this.failCloseSection('daily')
          this.requireLogin()
        } else if (isOkResponse(dailyRes)) {
          if (this.activeTab === 'daily') this.readFailureMessage = ''
          const dailyContent = responseContent(dailyRes)
          const dailyData = this.sectionData(dailyContent, 'daily')
          if (this.shouldBlockSectionRead(dailyRes, dailyData)) {
            this.dailyLoaded = true
          } else {
            if (!this.preserveUnavailableSectionRead('daily', dailyRes, dailyData) &&
              !this.preservePartialReadFailure('daily', hasPartialReadFailure, dailyData)) {
              this.parseSection('daily', dailyData, 'daily', this.isRewardRowWritable(dailyContent))
            }
          }
          this.dailyLoaded = true
        } else {
          this.failCloseSection('daily')
          if (!this.hasVisibleSectionData('daily')) this.dailyLoaded = false
          this.setReadFailure(dailyRes)
        }
        if (isNeedLogin(weeklyRes)) {
          this.failCloseSection('weekly')
          this.requireLogin()
        } else if (isOkResponse(weeklyRes)) {
          if (this.activeTab === 'weekly') this.readFailureMessage = ''
          const weeklyContent = responseContent(weeklyRes)
          const weeklyData = this.sectionData(weeklyContent, 'weekly')
          if (this.shouldBlockSectionRead(weeklyRes, weeklyData)) {
            this.weeklyLoaded = true
          } else {
            if (!this.preserveUnavailableSectionRead('weekly', weeklyRes, weeklyData) &&
              !this.preservePartialReadFailure('weekly', hasPartialReadFailure, weeklyData)) {
              this.parseSection('weekly', weeklyData, 'weekly', this.isRewardRowWritable(weeklyContent))
            }
          }
          this.weeklyLoaded = true
        } else {
          this.failCloseSection('weekly')
          if (!this.hasVisibleSectionData('weekly')) this.weeklyLoaded = false
          this.setReadFailure(weeklyRes)
        }
      } catch (e) {
        this.failCloseSection('daily')
        this.failCloseSection('weekly')
        if (!this.hasVisibleSectionData('daily')) this.dailyLoaded = false
        if (!this.hasVisibleSectionData('weekly')) this.weeklyLoaded = false
        this.setReadFailure(e, this.$lang.mc_request_failed || this.$lang.request_failed || 'Mission request failed. Please try again later.')
      }
    },
    switchTab(tab) {
      if (tab === this.activeTab) return
      reportPromoPanel(TAB_TO_PROMO[this.activeTab], 2)
      reportPromoPanel(TAB_TO_PROMO[tab], 1)
      if (tab !== 'newbie' && !this.newbieCompleted) {
        this.activeTab = tab
        return
      }
      this.activeTab = tab
    },
    parseSection(type, data, category, parentWritable = true) {
      this[type].category = category
      if (!data || typeof data !== 'object') {
        this[type].currentPoints = 0
        this[type].totalPoints = 0
        this[type].milestones = []
        this[type].tasks = []
        return
      }
      const sectionWritable = parentWritable && this.isRewardRowWritable(data)
      const milestones = this.milestoneRows(data)
      const tasks = this.firstArray(data, ['tasks', 'taskList', 'task_list', 'taskRows', 'task_rows', 'records', 'list', 'dataList'])
      this[type].currentPoints = this.safeNumber(this.firstValue(data, ['points', 'currentPoints', 'currentPoint', 'point', 'score', 'activityPoints', 'activity_points'], 0))
      this[type].totalPoints = this.safeNumber(
        this.firstValue(data, ['totalPoints', 'target', 'targetPoints', 'maxPoints', 'max_points', 'maxPoint', 'max_point'], 0)
      )
      this[type].milestones = milestones.map(m => ({
        ...m,
        sectionWritable
      })).map(m => ({
        threshold: this.safeNumber(this.firstValue(m, ['threshold', 'points', 'point', 'targetPoints', 'target_points', 'thresholdPoints', 'requiredPoints', 'minPoints'], 0)),
        rewardAmount: this.safeNumber(this.firstValue(m, ['rewardAmount', 'rewardGold', 'reward', 'prize', 'value', 'amount', 'bonus'], 0)),
        canClaim: this.isMilestoneClaimable(m),
        claimed: !!m.claimed || this.isTrueFlag(m.isClaimed) || ['claimed', 'completed', 'received'].includes(String(m.status || m.state || '').toLowerCase()),
        raw: m
      })).filter(m => m.threshold > 0).sort((a, b) => a.threshold - b.threshold)
      this[type].tasks = tasks.map(t => ({
        code: this.firstValue(t, ['code', 'taskCode', 'task_code', 'id'], ''),
        name: this.firstValue(t, ['name', 'taskName', 'task_name', 'title'], ''),
        desc: this.firstValue(t, ['desc', 'description', 'taskDesc', 'task_desc'], ''),
        progress: this.firstValue(t, ['progressText', 'progress', 'progressDesc', 'progress_desc'], ''),
        rewardAmount: this.safeNumber(this.firstValue(t, ['points', 'rewardPoints', 'reward_points', 'activityPoints', 'activity_points', 'claimAmount', 'claim_amount'], 0)),
        rewardGold: this.safeNumber(this.firstValue(t, ['rewardGold', 'rewardAmount', 'reward', 'amount', 'bonus', 'gold'], 0)),
        iconUrl: this.firstValue(t, ['iconUrl', 'icon', 'icon_url', 'imageUrl'], null),
        status: this.getTaskStatus(t, sectionWritable),
        rawStatus: t.status || t.state || '',
        canClaimReward: this.isTaskClaimable(t),
        actionUrl: this.firstValue(t, ['actionUrl', 'url', 'jumpUrl', 'route'], null),
        raw: t
      }))
    },
    milestoneRows(data) {
      const direct = this.firstArray(data, ['milestones', 'progressRewards', 'rewardMilestones', 'rewardList', 'rewards'])
      if (direct.length) return direct
      const track = data && data.rewardTrack
      if (Array.isArray(track)) return track
      if (track && typeof track === 'object') {
        return ['basic', 'free', 'base', 'junior', 'silver', 'premium', 'senior', 'gold', 'pro'].reduce((rows, key) => {
          const value = track[key]
          if (Array.isArray(value)) rows.push(...value)
          return rows
        }, [])
      }
      return []
    },
    isFalseFlag(value) {
      return value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase())
    },
    isRewardRowWritable(row) {
      if (!row || typeof row !== 'object') return false
      return !this.hasWriteGuardMarker(row) &&
        !this.isFalseFlag(row.rewardWritable) &&
        !this.isFalseFlag(row.writable) &&
        !this.isFalseFlag(row.writeEnabled)
    },
    isMilestoneClaimable(row) {
      if (row && row.sectionWritable === false) return false
      if (!this.isRewardRowWritable(row)) return false
      return this.isTrueFlag(row.canClaim) ||
        this.isTrueFlag(row.canReceive) ||
        this.isTrueFlag(row.claimable) ||
        this.isTrueFlag(row.canClaimReward)
    },
    isTaskClaimable(row) {
      if (!this.isRewardRowWritable(row)) return false
      return this.isTrueFlag(row.canClaimReward) ||
        this.isTrueFlag(row.canClaim) ||
        this.isTrueFlag(row.canReceive) ||
        this.isTrueFlag(row.claimable)
    },
    getTaskStatus(t, sectionWritable = true) {
      const rawStatus = String(t.status || t.state || t.rewardStatus || '').toLowerCase()
      if (t.status === 'completed' || t.claimed === true || this.isTrueFlag(t.isClaimed) || ['claimed', 'completed', 'received'].includes(rawStatus)) return 'claimed'
      if (!sectionWritable) return 'inProgress'
      if (!this.isRewardRowWritable(t)) return 'inProgress'
      if (this.isTaskClaimable(t)) return 'claimable'
      return 'inProgress'
    },
    startCountdown() {
      clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        const now = Date.now()
        const diff = Math.max(0, this.resetTs - now)
        if (diff === 0) {
          clearInterval(this.countdownTimer)
          this.resetCountdown = '00:00:00'
          return
        }
        const h = Math.floor(diff / 3600000)
        const m = Math.floor((diff % 3600000) / 60000)
        const s = Math.floor((diff % 60000) / 1000)
        this.resetCountdown =
          String(h).padStart(2, '0') + ':' +
          String(m).padStart(2, '0') + ':' +
          String(s).padStart(2, '0')
      }, 1000)
    },
    async handleTaskAction(task) {
      if (this.claimingTaskCode) return
      if (task && task.status === 'claimable' && !this.isRewardRowWritable(task.raw || task)) {
        this.$toast({ message: this.failMessage(task.raw || task, this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'), icon: 'cross' })
        return
      }
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (task.status === 'inProgress') {
        const route = await this.getTaskRoute(task.code)
        if (route && typeof route === 'object') {
          this.$jumpTo(route.path, route.query || {})
        } else if (route) {
          this.$jumpTo(route)
        } else if (task.code !== 'NEWBIE_WITHDRAW_1' && task.actionUrl) {
          this.$jumpTo(task.actionUrl)
        }
        return
      }
      if (task.status === 'claimable') {
        this.claimingTaskCode = task.code
        try {
          const res = await ClaimTaskReward({
            category: this.currentSection.category || this.activeTab,
            taskCode: task.code
          })
          if (isNeedLogin(res)) {
            this.requireLogin()
          } else if (this.isClaimSuccess(res)) {
            const content = responseContent(res)
            const amount = (content.amount || content.rewardAmount || content.reward) || task.rewardAmount
            this.$toast({ message: `+${this.$formatNumberWithCommas(amount)} ${this.getCurrency || ''}`, icon: 'success' })
            task.status = 'claimed'
            this.currentSection.currentPoints += task.rewardAmount
          } else {
            this.$toast({ message: this.failMessage(res, this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'), icon: 'cross' })
          }
        } catch (e) {
          const networkFallback = this.$lang.mc_request_failed || this.$lang.network_error || 'Mission request failed. Please try again later.'
          this.$toast({ message: this.failMessage(e, networkFallback), icon: 'cross' })
        } finally {
          this.claimingTaskCode = ''
        }
      }
    },
    async getTaskRoute(code) {
      if (code === 'NEWBIE_WITHDRAW_1') {
        return this.getWithdrawTaskRoute()
      }
      const routeMap = {
        NEWBIE_INVITE_1: '/luckyReferral',
        NEWBIE_FIRST_RECHARGE: '/rechargeCont',
        DAILY_RECHARGE: '/rechargeCont',
        DAILY_BET: '/home',
        WEEKLY_RECHARGE: '/rechargeCont',
        WEEKLY_BET: '/home'
      }
      return routeMap[code] || null
    },
    async getWithdrawTaskRoute() {
      try {
        const res = await WithdrawInit({})
        if (isNeedLogin(res)) {
          this.requireLogin()
          return null
        }
        if (!isOkResponse(res)) {
          this.$toast({ message: this.failMessage(res), icon: 'cross' })
          return null
        }
        const route = getMineActionRoute('withdraw', {
          loggedIn: !!this.token,
          profile: responseContent(res),
          source: 'weeklyMissions'
        })
        if (route.requiresLogin) {
          this.requireLogin()
          return null
        }
        return route
      } catch (e) {
        this.$toast({ message: this.failMessage(e, this.$lang.mc_request_failed || this.$lang.request_failed || 'Mission request failed. Please try again later.'), icon: 'cross' })
        return null
      }
    },
    async claimMilestone(m) {
      if (this.claimingMilestoneKey) return
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (m.claimed) return
      if (!this.isRewardRowWritable(m.raw || m)) {
        this.$toast({ message: this.failMessage(m.raw || m, this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'), icon: 'cross' })
        return
      }
      if (!m.canClaim) {
        this.$toast({ message: this.failMessage(m.raw || m, this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'), icon: 'cross' })
        return
      }
      this.claimingMilestoneKey = `${this.currentSection.category || this.activeTab}:${m.threshold}`
      try {
        const res = await ClaimProgressReward({
          category: this.currentSection.category || this.activeTab,
          threshold: m.threshold
        })
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isClaimSuccess(res)) {
          const content = responseContent(res)
          const amount = (content.amount || content.rewardAmount || content.reward) || m.rewardAmount
          this.$toast({ message: `+${this.$formatNumberWithCommas(amount)} ${this.getCurrency || ''}`, icon: 'success' })
          m.claimed = true
        } else {
          this.$toast({ message: this.failMessage(res, this.$lang.mc_claim_unavailable || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        const networkFallback = this.$lang.mc_request_failed || this.$lang.network_error || 'Mission request failed. Please try again later.'
        this.$toast({ message: this.failMessage(e, networkFallback), icon: 'cross' })
      } finally {
        this.claimingMilestoneKey = ''
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;  /* 绿→火焰橙(强调/激活/CTA) */
@gold: #ffb020;    /* 黄→金(金额/奖) */
@bg: @vk-bg;       /* 页面底 */
@card: @vk-surface;/* 卡片面 */

.mc-page {
  min-height: 100vh;
  background: @bg;
  padding-bottom: 40px;
}

.mc-loading {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.mc-empty {
  padding: 32px 12px;
  text-align: center;
  color: var(--vk-t3);
  font-size: var(--vk-fs-sm);
}

.mc-read-warning {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border: 1px solid fade(@gold, 30%);
  border-radius: 8px;
  background: fade(@gold, 8%);
  color: @gold;
  font-size: var(--vk-fs-xs);
  line-height: 1.4;
}

// ====== HEADER ======
.mc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: @bg;

  &__back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: 700;
    letter-spacing: 2px;
    color: #ffffff;
    text-transform: uppercase;
  }

  &__spacer { width: 40px; }
}

// ====== TABS ======
.mc-tabs {
  display: flex;
  margin: 0 16px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;

  &__btn {
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border: none;
    background: none;
    color: var(--vk-t3);
    cursor: pointer;
    transition: all 0.2s;

    &--active {
      background: @primary;
      color: #000000;
      box-shadow: 0 0 12px rgba(255, 90, 31, 0.4);
    }

    &--locked {
      color: #4b5563;
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}

// ====== LOCKED STATE ======
.mc-locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: 700;
    color: #ffffff;
    margin-top: 16px;
    margin-bottom: 8px;
  }

  &__desc {
    font-size: var(--vk-fs-sm);
    color: var(--vk-t3);
    margin-bottom: 24px;
    line-height: 1.5;
  }

  &__btn {
    background: @primary;
    color: #000000;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 10px 24px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    box-shadow: 0 0 12px rgba(255, 90, 31, 0.4);

    &:active {
      transform: scale(0.96);
    }
  }
}

// ====== CUMULATIVE ======
.mc-cumulative {
  margin: 0 16px 16px;
  background: @card;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255,255,255,0.08);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__label {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--vk-t3);
  }

  &__reset {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
  }

  &__score {
    margin-bottom: 16px;
  }

  &__current {
    font-size: 28px;
    font-weight: 900;
    color: @primary;
  }

  &__sep {
    font-size: var(--vk-fs-lg);
    color: #4b5563;
    margin: 0 2px;
  }

  &__total {
    font-size: var(--vk-fs-lg);
    color: #9ca3af;
  }
}

// ====== MILESTONES ======
.mc-milestones {
  position: relative;
  height: 70px;
  margin-bottom: 8px;
}

.mc-milestone {
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  opacity: 0.4;
  filter: grayscale(1);
  transition: all 0.3s;

  &--reached {
    opacity: 1;
    filter: none;
    animation: chest-glow 2s ease-in-out infinite;
  }

  &--claimed {
    opacity: 0.7;
    filter: none;
  }

  &__chest {
    position: relative;
    width: 40px;
    height: 40px;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__check {
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: @bg;
    border-radius: 50%;
  }

  &__pts {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    color: #9ca3af;
  }

  &__reward {
    position: absolute;
    bottom: -14px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 8px;
    font-weight: 700;
    color: @gold;
    white-space: nowrap;
  }
}

@keyframes chest-glow {
  0%, 100% { filter: drop-shadow(0 0 6px rgba(255, 90, 31,0.6)); }
  50% { filter: drop-shadow(0 0 14px rgba(255, 90, 31,1)); }
}

// ====== PROGRESS BAR ======
.mc-bar {
  background: rgba(255,255,255,0.08);
  border-radius: 50px;
  height: 8px;
  overflow: hidden;
  margin-top: 4px;

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #16a34a, @primary);
    border-radius: 50px;
    transition: width 0.6s ease;
    box-shadow: 0 0 8px rgba(255, 90, 31,0.5);
  }
}

// ====== TASKS ======
.mc-tasks {
  padding: 0 16px;
  margin-bottom: 16px;

  &__title {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--vk-t3);
    margin-bottom: 12px;
    padding-left: 4px;
  }
}

.mc-task {
  display: flex;
  align-items: center;
  gap: 12px;
  background: @card;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  border: 1px solid rgba(255,255,255,0.05);
  transition: opacity 0.2s;

  &--claimable {
    border-left: 4px solid @primary;
  }

  &--claimed {
    opacity: 0.6;
  }

  &__icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    background: rgba(255, 90, 31,0.1);
    border: 1px solid rgba(255, 90, 31,0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 2px;
  }

  &__progress {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
  }

  &__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
  }

  &__rewards {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  &__reward {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    color: @gold;

    &--pts {
      color: @gold;
    }

    &--gold {
      color: @primary;
    }
  }

  &__btn {
    font-size: var(--vk-fs-2xs);
    font-weight: 900;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid rgba(255, 90, 31,0.4);
    background: rgba(255, 90, 31,0.1);
    color: @primary;
    cursor: pointer;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--claimable {
      background: @primary;
      color: #000000;
      border-color: @primary;
      box-shadow: 0 0 8px rgba(255, 90, 31,0.4);
    }

    &--claimed {
      background: rgba(255,255,255,0.05);
      border-color: transparent;
      color: #4b5563;
      cursor: not-allowed;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }
}

// ====== RULES ======
.mc-rules {
  padding: 0 16px;

  &__card {
    background: @card;
    border-radius: 10px;
    padding: 16px;
    border: 1px solid rgba(255,255,255,0.05);
  }

  &__heading {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--vk-t3);
    margin-bottom: 12px;
  }

  &__list {
    padding-left: 16px;
    margin: 0;

    li {
      font-size: var(--vk-fs-xs);
      color: #9ca3af;
      line-height: 1.8;
    }
  }
}

@media (min-width: 769px) {
  .mc-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
